<template>
    <LoadingModal
        v-if="loading"
        header="Bulk Import Entities"
        open
        @close="close"
    />
    <BulkImportEntitiesModal
        v-else-if="opened"
        :custom-properties="sortedCustomProperties"
        :requirement-types="nonMultiInstanceRequirementTypes"
        :saving="saving"
        :saving-percentage="upsertEntitiesStatus.progress"
        :external-alert-config="alertConfig"
        :contact-email-uniqueness-enabled="contactEmailUniquenessEnabled"
        :producer-coverage-types="producerCoverageTypes"
        :producer-coverage-type-labels="producerCoverageTypeLabels"
        :find-blocked-producer-emails="findBlockedProducerEmails"
        @close="close"
        @submit="importEntities"
        @input="clearAlert"
    />
</template>
<style lang="scss">
    .BulkImportEntitiesModal, .BulkImportCorrectionTable {
        .Alert {
            min-height: 50px;
            padding: 12px 20px;
            display: flex;
            align-items: center;
            justify-content: center;

            > h4:first-child {
                font-size: 1em;
            }

            svg {
                font-size: 1em;
                margin-right: 5px;
            }

            &__description {
                font-weight: 500;
            }
        }
    }
</style>
<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import mapValues from 'lodash/mapValues';

    import { AlertType } from '@evidentid/dashboard-commons/components/Alert/types';
    import { TprmRequirementDefinition } from '@evidentid/tprm-portal-lib/models/common';
    import { CustomProperty, EntityInput, UiConfig } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import LoadingModal from '@/components/loading-modal/LoadingModal.vue';
    import { EnumCategories } from '@/modules/dashboard/models/EnumCategories.model';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import {
        LEGACY_CSV_FORMAT_MESSAGE,
    } from '@/modules/entity-management/constants/entityMessages.constant';
    import {
        BatchUpsertEntitiesStatus,
        createEmptyUpsertEntitiesStatus,
        useEntityManagementStore,
    } from '@/modules/entity-management/store';
    import {
        getProducerCoverageTypes,
    } from '@/modules/entity-management/utils/producers/getProducerCoverageTypes';
    import { getSortedCustomProperties } from '@/utils/get-sorted-custom-properties/getSortedCustomProperties';

    import BulkImportEntitiesModal from '../components/BulkImportEntitiesModal/BulkImportEntitiesModal.vue';

    // PRODUCT-26110 QA fixes4: declare every event this procedure `$emit`s, or Vue 3 warns
    // that it is "neither declared in the emits option nor as an 'onAbort'/'onFinish' prop".
    @Component({
        emits: [ 'abort', 'finish' ],
        components: { BulkImportEntitiesModal, LoadingModal },
    })

    export default class BulkImportEntitiesProcedure extends Vue {
        // ProcedureTarget passes `:uuid` to every procedure it renders. Nothing here reads
        // it, but both roots below are conditional, so before the modal opens this renders
        // a comment node -- an undeclared `uuid` would then have nowhere to land and Vue
        // warns about extraneous non-prop attributes.
        @Prop({ type: String, default: '' })
        private uuid?: string;

        @Prop({ type: String, default: '' })
        private rpName!: string;

        private opened: boolean = true;
        private saving: boolean = false;
        private alertConfig: { type: AlertType, title: string } | null = null;
        private sampleEntities: EntityInput[] = [];
        private needCollectEvidence: boolean = false;

        private clearAlert(): void {
            this.alertConfig = null;
        }

        private showUploadModalAlert(title: string): void {
            this.alertConfig = { type: 'danger', title };
        }

        @Watch('upsertEntitiesStatus')
        private async onUpdateEntitiesStatusChange(upsertEntitiesStatus: BatchUpsertEntitiesStatus) {
            this.saving = upsertEntitiesStatus.status === OperationStatus.loading;
            if (upsertEntitiesStatus.status === OperationStatus.success) {
                // Rows refused individually are reported by the entities list, which reloads
                // anyway for the rows that were written.
                this.$emit('finish', {
                    sampleEntities: this.sampleEntities,
                    collectEvidence: this.needCollectEvidence,
                });
            } else if (upsertEntitiesStatus.status === OperationStatus.error) {
                // invalidPayload is any HTTP 400 on a chunk, not specifically a missing id. Only a
                // non-unique relying party can be rejected for that, so only there does pointing
                // the client at a fresh worksheet help - elsewhere it sends them after a column
                // their template does not carry (PRODUCT-26313).
                const askForNewWorksheet =
                    upsertEntitiesStatus.invalidPayload && !this.contactEmailUniquenessEnabled;
                this.showUploadModalAlert(askForNewWorksheet
                    ? LEGACY_CSV_FORMAT_MESSAGE
                    : 'Sorry, something went wrong during import. Please try again later.');
            }
        }

        private get upsertEntitiesStatus(): BatchUpsertEntitiesStatus {
            return useEntityManagementStore().upsertEntitiesStatus[this.rpName]
                || createEmptyUpsertEntitiesStatus();
        }

        private get customPropertiesStatus(): { status: OperationStatus, list: CustomProperty[] } {
            return useDashboardStore().customProperties[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get customPropertiesDisplayStatus(): { status: OperationStatus, data: UiConfig } {
            const config = useDashboardStore().rpConfig[this.rpName];
            return {
                status: config?.status ?? OperationStatus.uninitialized,
                data: {
                    customPropertiesOrder: config?.customPropertiesOrder ?? [],
                    entitiesTableCustomProperties: config?.entitiesTableCustomProperties ?? [],
                },
            };
        }

        private get sortedCustomProperties(): CustomProperty[] {
            return getSortedCustomProperties(
                this.customPropertiesStatus.list,
                this.customPropertiesDisplayStatus.data.customPropertiesOrder,
            );
        }

        // Decides whether the worksheet carries an Entity ID column and what a blank one means
        // (PRODUCT-26313). Absent config reads as enforced, which keeps older worksheets importable.
        private get contactEmailUniquenessEnabled(): boolean {
            return useDashboardStore().rpConfig[this.rpName]?.contactEmailUniquenessEnabled ?? true;
        }

        private get clientRequirementTypes(): { status: OperationStatus, list: string[] } {
            return useDashboardStore().clientRequirementTypes[this.rpName] || {
                status: OperationStatus.uninitialized,
                list: [],
            };
        }

        private get requirementDefinitions(): Record<string, TprmRequirementDefinition> {
            return useDashboardStore().tprmRequirementsDefinitions || {};
        }

        private get nonMultiInstanceRequirementTypes(): string[] {
            return this.clientRequirementTypes.list.filter(
                (type) => !this.requirementDefinitions[type]?.multiInstance,
            );
        }

        private get producerCoverageTypes(): string[] | null {
            return getProducerCoverageTypes(
                (this as any).$rp?.currentDetails?.producerFlowEnabled,
                this.clientRequirementTypes.list,
            );
        }

        // The portal loads these labels at start-up. A key with no label is matched by the key alone.
        private get producerCoverageTypeLabels(): Record<string, string> {
            const labels = useDashboardStore().categorizedEnumLabels[EnumCategories.coverageTypes] || {};
            return mapValues(labels, (labelObject) => labelObject.label);
        }

        private get loading(): boolean {
            return this.customPropertiesStatus.status === OperationStatus.loading
                || this.clientRequirementTypes.status === OperationStatus.loading
                // The RP config decides the worksheet's columns and what a blank Entity ID cell
                // means, and neither reading of a config we have not actually loaded is safe -
                // hence success rather than "not loading" (PRODUCT-26313). A load that fails
                // closes the procedure, so this cannot sit here indefinitely.
                || this.customPropertiesDisplayStatus.status !== OperationStatus.success;
        }

        @Watch('customPropertiesDisplayStatus')
        private onRpConfigStatusChange(rpConfigStatus: { status: OperationStatus }): void {
            // loadRpConfig reports the failure itself. Without the config there are no import
            // rules to offer, so the procedure closes instead of guessing them.
            if (rpConfigStatus.status === OperationStatus.error) {
                this.close();
            }
        }

        private mounted() {
            if (this.customPropertiesStatus.status !== OperationStatus.loading) {
                useDashboardStore().loadCustomProperties({ rpName: this.rpName });
            }
            if (this.clientRequirementTypes.status !== OperationStatus.loading) {
                useDashboardStore().loadClientRequirementTypes({ rpName: this.rpName });
            }
            if (this.customPropertiesDisplayStatus.status !== OperationStatus.loading) {
                useDashboardStore().loadRpConfig({ rpName: this.rpName });
            }
        }

        private close(): void {
            this.opened = false;
            this.$emit('abort');
        }

        private findBlockedProducerEmails(emails: string[]): Promise<string[]> {
            return useEntityManagementStore().validateProducerEmails({ rpName: this.rpName, emails });
        }

        // The "Entity ID" cell of each row decides whether it creates or updates an entity, so
        // one upsert call covers the whole file.
        private importEntities(
            entities: EntityInput[],
            sampleEntities: EntityInput[],
            hasPastExpiration: boolean,
        ) {
            this.sampleEntities = sampleEntities;
            this.needCollectEvidence = hasPastExpiration;
            useEntityManagementStore().updateEntities({ rpName: this.rpName, entities, strictProducerChecks: true });
        }
    }
</script>
